/**
 * THE WORK, AS A LIST — and the quote pile, which is the same list asked a
 * narrower question.
 *
 * The old system had a Jobs page and a Quotes page built from different
 * code that drifted apart: one showed the vehicle, the other did not; one
 * knew about archived rows, the other did not. A quote is a job at status
 * `estimate`. Same list, same row, different filter.
 */

import { listJobs, setStatus, STATUS_LABEL, stateLabel, vehicleName, whereLabel, type JobRow, type Status } from "../data/jobs.ts";
import { db } from "../data/client.ts";
import { newJobSheet, type NewKind } from "./newjob.ts";
import { icon } from "../ui/icons.ts";
import { esc, money, ago, until, mount, since } from "../ui/dom.ts";
import { picker, sortsFor, orderFor, type SortKey } from "../ui/picker.ts";
import { quoteStateOf, quoteStateLabel, QUOTE_STATES, type QuoteState }
  from "../domain/quotestate.ts";
import { spanFrom, SPANS, DATED_BY, type Span } from "../data/funnel.ts";
import { floorFor } from "../domain/watermark.ts";
import { viewOf, viewToggleHtml, wireViewToggle } from "../ui/viewmode.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import { shopWords } from "../ui/words.ts";
import { thumbHtml, fillThumbs } from "../ui/carthumb.ts";
import { isPpiJob, advisorLine } from "../domain/ppi.ts";

/* The sort survives moving between Jobs and Quotes, because they are the
   same list asked a narrower question and it would be strange if it did not. */
let sort: SortKey = "new";

/**
 * A WEEK, AND THE REST BEHIND A BUTTON.
 *
 * Anthony: "Right now, my staff only needs to see the data from the last
 * week anywhere. Anything older needs to be behind load more."
 *
 * The lists loaded five hundred rows and drew all of them, which is not a
 * list, it is an archive with a search box on top. Nineteen hundred jobs
 * means the thing somebody wants is on screen with fourteen hundred things
 * nobody wants, and on a phone it is a page that takes a second to paint
 * before it can be scrolled.
 *
 * A WEEK IS THE DEFAULT, NOT A CAGE. The button says how many are older,
 * because a list that quietly stops short is how work goes missing — and
 * pressing it loads the lot. Searching still reaches every job in the shop
 * whatever this is set to, because a search that only looks at this week
 * is the bug that was fixed two days ago.
 */
const WEEK_MS = 7 * 864e5;
/**
 * WHICH LIST HAS BEEN OPENED PAST THE WEEK, not whether one has.
 *
 * Anthony, 2026-09-09: "show older on jobs doesn't work."
 *
 * It did not, and could not. This was a bare boolean, and renderJobs set it
 * back to false at the top of every run -- including the run the button
 * itself kicked off. Press it, and the only thing that happened was the
 * list redrawing exactly as it was. The same bug ate the "never sent, worth
 * $X" button, which is the worse one: that is the neglected money, and the
 * one press that goes and finds it did nothing at all.
 *
 * renderJobs is both the way onto the screen and the way the screen
 * redraws, so a reset at the top of it cannot tell "somebody arrived" from
 * "somebody changed the sort". Holding the NAME of the opened list instead
 * of a yes/no answers both: a redraw of the same list keeps it open, and
 * moving to a different list starts back on the week, which is the rule
 * that was wanted in the first place.
 */
let openedOn = "";

/**
 * WHICH PILE OF QUOTES. Anthony: "Allow for sort between quote status.
 * statuses include not sent, sent, approved, modifications requested,
 * cancelled."
 *
 * Empty means all of them, which is what the page has always shown, so
 * nobody who ignores the band notices it arrived. It is remembered for
 * the session rather than the URL, the same way the sort and the view
 * toggle are, because it is a way of working rather than a place.
 */
let quotePile: QuoteState | "" = "";

/**
 * WHAT THE PILL SAYS.
 *
 * Anthony: "Update quotes that are sent to say sent and show this on
 * cards."
 *
 * Every estimate said "Quote" -- a price nobody has sent, a price sent
 * three days ago with no answer, and a price the customer has already
 * agreed to were the same word on the same card. The five quote states
 * were built and were only ever wired to the filter pills at the top of
 * the screen, so you could sort by them but never see one.
 *
 * The state is derived, not stored, so the card cannot disagree with the
 * authorization sitting next to it.
 */
function pill(j: JobRow): string {
  if (j.status !== "estimate") {
    return `<span class="pill s-${esc(j.status)}">${
      esc(stateLabel(j.status, j.archived_at))}</span>`;
  }
  const st = stateOf(j);
  return `<span class="pill q-${esc(st)}" title="${
    esc(QUOTE_STATES.find((x) => x.key === st)?.blurb ?? "")}">${
    esc(quoteStateLabel(st))}</span>`;
}

/**
 * HOW LONG, AND WHOSE MOVE IT IS.
 *
 * "6 days ago" on a quote answers the wrong question. A price that was
 * written and never sent is the shop's fault and needs a person; a price
 * that went out and got no answer is the customer's move and needs
 * chasing. Both used to read the same, and 82 quotes worth $52,520 sat in
 * one undifferentiated pile.
 *
 * The send time is NOT derived from quote_expires_at. That is editable, so
 * extending a quote would move a date the shop would then read as fact.
 */
const COLD_MS = 3 * 24 * 60 * 60_000;
const isCold = (j: JobRow): boolean =>
  j.status === "estimate" && stateOf(j) === "not_sent"
  && Date.now() - Date.parse(j.created_at) > COLD_MS;

function waiting(j: JobRow): string {
  if (j.status !== "estimate") {
    return j.status === "scheduled" && j.scheduled_at ? until(j.scheduled_at)
      : j.completed_at ? `${since(j.completed_at)}` : `${since(j.created_at)}`;
  }
  const st = stateOf(j);
  if (st === "not_sent") return `priced ${since(j.created_at)}, never sent`;
  if (st === "changes") return "they asked for changes";
  if (st === "sent") return `waiting on them, ${ago(j.created_at)}`;
  return `${since(j.created_at)}`;
}

/**
 * TWO QUOTES FOR THE SAME CAR.
 *
 * Anthony: "there is an issue with duplicate quotes and no option to
 * archive anything."
 *
 * The making of them is stopped at the source, in convertToQuote. These
 * are the ones already on the board, and they are only findable by eye
 * today — the list sorts by date, so the twin of a quote raised on Friday
 * can be six rows further down. Same customer, same car, both still open:
 * that is a duplicate, and saying so on the row is what makes the archive
 * button beside it obvious rather than alarming.
 *
 * Deliberately NOT matched on title. "Brakes" and "front brakes" are the
 * same job written twice, which is exactly the case worth catching.
 */
const OPEN_FOR_DUPES = ["estimate", "scheduled"];

/**
 * WHAT CAN BE ARCHIVED. Anthony: "1606 is not a job and needs to be
 * archived."
 *
 * Junk does not only arrive as a quote. A test record, a lead converted
 * twice, a call that became a job by accident — any of them can have been
 * scheduled or even started before somebody noticed. Restricting the
 * button to estimates meant the ones most obviously not jobs were the
 * ones you could not clear.
 *
 * It stops at money. Anything completed, invoiced or paid has a figure
 * attached and belongs in the ledger, so those are moved with the status
 * control on the job itself, deliberately and with a reason.
 */
const ARCHIVABLE = ["estimate", "scheduled", "in_progress"];

const dupeKey = (j: JobRow): string =>
  `${j.customers?.full_name ?? ""}|${vehicleName(j.vehicles)}`.toLowerCase();

let dupes = new Set<string>();

/* Whether the person looking at this list gets the technician's path.
   Set by the router, which already knows the role, rather than threaded
   through jobsPage and every caller of it that does not otherwise care. */
let walker = false;
export const setWalker = (on: boolean): void => { walker = on; };

/* Whether this person may end a job. Archive sets `cancelled`, which is a
   cancel however friendly the button is called — a technician pressing it
   ends work a customer may have agreed to pay for. `setStatus` refuses it
   outright; this only stops us OFFERING a button that throws, which is a
   worse way to learn a rule than not seeing the button. Set by the router
   beside `walker`, for the same reason. */
let mayEnd = true;
export const setMayEnd = (on: boolean): void => { mayEnd = on; };

/**
 * WHO HAS EACH INSPECTION, on the row. Two desks work pre-purchase
 * inspections (src/domain/ppi.ts), and the list is where somebody decides
 * which one to pick up next -- so "Advisor: Eber Curiel (via MechanicRank
 * desk)" is on the row, not only inside the job. One read for the names
 * and one for the claims, for every inspection on the screen.
 */
let ppiWho = new Map<string, string>();
async function markAdvisors(list: JobRow[]): Promise<void> {
  ppiWho = new Map();
  const ppis = list.filter((j) => isPpiJob({ title: j.title, complaint: j.complaint }) && j.advisor_id);
  if (!ppis.length) return;
  try {
    const sb = await db();
    const [{ data: people }, { data: evs }] = await Promise.all([
      sb.from("profiles").select("id,full_name").in("id", [...new Set(ppis.map((j) => j.advisor_id!))]),
      sb.from("job_events").select("job_id,actor_id,meta,created_at").in("job_id", ppis.map((j) => j.id))
        .in("kind", ["claimed", "reassigned"]).order("created_at", { ascending: true }),
    ]);
    const name = new Map(((people ?? []) as Array<{ id: string; full_name: string }>).map((p) => [p.id, p.full_name]));
    const via = new Map<string, { actor: string | null; via: string }>();
    for (const e of (evs ?? []) as Array<{ job_id: string; actor_id: string | null; meta: Record<string, unknown> | null }>) {
      via.set(e.job_id, { actor: e.actor_id, via: String(e.meta?.["via"] ?? "shop-desk") });
    }
    for (const j of ppis) {
      const v = via.get(j.id);
      ppiWho.set(j.id, advisorLine(name.get(j.advisor_id!) ?? null,
        v && v.actor === j.advisor_id ? v.via : "shop-desk"));
    }
  } catch { /* the row still renders; it just does not say who */ }
}

function markDupes(list: JobRow[]): void {
  const seen = new Map<string, string[]>();
  for (const j of list) {
    if (!OPEN_FOR_DUPES.includes(j.status)) continue;
    const k = dupeKey(j);
    // A quote with no customer AND no car is not a duplicate of every
    // other quote with no customer and no car.
    if (k === "|") continue;
    seen.set(k, [...(seen.get(k) ?? []), j.id]);
  }
  dupes = new Set([...seen.values()].filter((v) => v.length > 1).flat());
}

/** The same column the railway counted on, so the list matches the number. */
function row(j: JobRow): string {
  const total = j.totals.total;
  const when = waiting(j);
  const twin = dupes.has(j.id);

  return `
    <a class="row${isCold(j) ? " coldquote" : ""}${twin ? " twinquote" : ""}"
       href="#/job/${j.id}">
      ${thumbHtml(j.id, j.vehicles)}
      <div class="row-main">
        <div class="row-title">
          <span class="num">${esc(j.job_number)}</span>
          ${pill(j)}
          ${twin ? `<span class="pill warn">Possible duplicate</span>` : ""}
          ${shopWords().caseFile ? "" : `<span class="wherebadge sm ${j.service_at ?? "unset"}">${
            icon(j.service_at === "shop" ? "shop" : "mobile")}${esc(whereLabel(j.service_at))}</span>`}
        </div>
        <div class="row-sub">
          ${esc(j.customers?.full_name ?? "No customer")}
          <span class="dot">&middot;</span>${esc(vehicleName(j.vehicles))}
        </div>
        ${j.title ? `<div class="row-note">${esc(j.title)}</div>` : ""}
        ${ppiWho.has(j.id) ? `<div class="row-note quiet">${esc(ppiWho.get(j.id)!)}</div>` : ""}
      </div>
      <div class="row-side">
        <!-- A free estimate visit has no money on it by design. -->
        <div class="amount">${j.visit ? "Free estimate" : money(total)}</div>
        <div class="when">${esc(when)}</div>
        <!-- ARCHIVE, not delete. It sets the same cancelled status the
             quote screen has always set, so nothing new can happen to
             the record and nothing is lost — a job archived by mistake
             is still there to be found and reopened. It lives on the row
             because clearing five duplicates should not mean opening
             five jobs and typing a reason into five prompts. -->
        <!-- EASY TO FIND. Anthony: "easy to find stuff."
             A technician's way into a job was one link on one screen you
             had to open the job to reach. The list is where they actually
             start their day, so the walk starts here too. Only for people
             who get the path, and only on work that has actually been
             booked in — a quote nobody has agreed to is not a job to walk. -->
        ${walker && ["scheduled", "in_progress"].includes(j.status)
          ? `<a class="rowwalk" href="#/tech/${esc(j.id)}"
               title="Walk this job step by step">${
                 j.status === "in_progress" ? "Carry on" : "Start"}</a>` : ""}
        ${mayEnd && ARCHIVABLE.includes(j.status)
          ? `<button class="rowarch" data-arch="${esc(j.id)}"
               title="Archive this one">Archive</button>` : ""}
        <!-- ARCHIVE EXISTS IN BOTH VIEWS NOW. Anthony: "where does the
             archive button live". It lived on the row and nowhere else,
             so switching to tiles made it vanish, which is indistinguish-
             able from it not existing. See tile() below. -->
      </div>
    </a>`;
}

/**
 * Wired after every render, on whatever rows are on screen. The button
 * sits inside the row's anchor, so the first job is to stop the click
 * becoming navigation to the job we are trying to get off the board.
 */
function wireArchive(redraw: () => void): void {
  document.querySelectorAll<HTMLButtonElement>("[data-arch]").forEach((b) =>
    b.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = b.dataset["arch"] ?? "";
      if (b.dataset["sure"] !== "1") {
        b.dataset["sure"] = "1";
        b.textContent = "Sure?";
        b.classList.add("armed");
        // One tap elsewhere and it forgets, so a stray tap on a phone in
        // a pocket cannot be half of a two-tap confirmation.
        setTimeout(() => {
          if (b.dataset["sure"] === "1") {
            b.dataset["sure"] = ""; b.textContent = "Archive";
            b.classList.remove("armed");
          }
        }, 4000);
        return;
      }
      b.disabled = true; b.textContent = "Archiving…";
      try {
        await setStatus(id, "cancelled",
          { hasAuthorization: false, hasPricedLine: false },
          "Archived from the jobs list.",
          // This is the board being tidied, not the work being called off.
          // Without it an approved job archived from here rings the same
          // red bell as a cancellation, which is what happened four times
          // in eleven seconds on 2026-09-18.
          { archiving: true });
        redraw();
      } catch (err) {
        b.disabled = false;
        b.textContent = err instanceof Error ? err.message.slice(0, 40) : "Failed";
      }
    }));
}

/**
 * THE SAME JOB, AS A TILE.
 *
 * Four facts and nothing else: who, what car, how much, and when. That is
 * the set somebody is actually deciding on when they look at a job they
 * have not opened — everything further belongs on the job itself.
 *
 * The money is the loudest thing on it because on every screen where
 * tiles beat rows, the money is what the eye is hunting for.
 */
function tile(j: JobRow): string {
  const when =
    j.status === "scheduled" && j.scheduled_at ? until(j.scheduled_at)
    : j.completed_at ? `${since(j.completed_at)}`
    : `${since(j.created_at)}`;

  return `
    <a class="jtile s-${esc(j.status)}" href="#/job/${j.id}">
      <div class="jtile-head">
      ${thumbHtml(j.id, j.vehicles, "tile")}
      <div class="jtile-id">
      <div class="jtile-top">
        <span class="num">${esc(j.job_number)}</span>
        ${j.status === "estimate" ? pill(j) : ""}
        ${shopWords().caseFile ? "" : `<span class="wherebadge sm ${j.service_at ?? "unset"}">${
          icon(j.service_at === "shop" ? "shop" : "mobile")}${esc(whereLabel(j.service_at))}</span>`}
      </div>
      <div class="jtile-who">${esc(j.customers?.full_name ?? "No customer")}</div>
      <div class="jtile-car">${esc(vehicleName(j.vehicles))}</div>
      </div>
      </div>
      <div class="jtile-foot">
        <span class="amount">${j.visit ? "Free estimate" : money(j.totals.total)}</span>
        <span class="when">${esc(when)}</span>
        ${mayEnd && ARCHIVABLE.includes(j.status)
          ? `<button class="rowarch tilearch" data-arch="${esc(j.id)}"
               title="Archive this one">Archive</button>` : ""}
      </div>
    </a>`;
}

function empty(what: string): string {
  return `<div class="empty">Nothing ${what}. <span>&#127942;</span></div>`;
}

export async function renderJobs(opts: {
  statuses?: Status[]; span?: Span;
  /** What the add button says, and what it makes. */
  newWord?: string;
  newKind?: NewKind;
  heading: string; blurb: string; emptyWord: string;
  /**
   * For a stage whose members cannot be expressed as "status plus a date
   * column" — Paid is the one: it is decided by when the MONEY arrived,
   * which lives on payments, not on the job.
   */
  ids?: () => Promise<string[]>;
}): Promise<void> {
  const p = picker(sortsFor(["new", "old"]), sort, { label: "Sort" });
  const quoteBand = opts.statuses?.[0] === "estimate";
  // One remembered setting per status, so "schedule as tiles, invoices as
  // rows" is a thing a person can have.
  const listKey = `jobs:${opts.statuses?.[0] ?? "all"}`;
  const view = viewOf(listKey);

  mount(`<div class="page"><header class="page-head">
      ${opts.span ? `<a class="back" href="#/">&larr; Desk</a>` : ""}
      <h1>${esc(opts.heading)}</h1><p>${esc(opts.blurb)}</p>
      <!-- Work that did not announce itself. Every job in here arrived
           through a webhook, a lead or a bridge, and half a shop's work
           walks in the door instead.

           IT SAYS WHICH ONE YOU ARE ON. This read "Start a job" on the
           Quotes page too, and somebody looking to write a quote does not
           see a quote button there — so as far as the shop was concerned
           there was no way to make one. -->
      <button class="btn primary newjob" id="newjob">${
        opts.newWord ?? "Start a job"}</button></header>
      <div id="findhost">${findBarHtml(
        `Search every job — name, ${shopWords().thing}, number, what they said`)}</div>
      ${quoteBand ? `<div class="crewbar" id="qstates"></div>` : ""}
      <div class="listbar" id="listbar">${p.html}
        <span class="pager-note" id="count"></span>
        ${viewToggleHtml(listKey)}</div>
      <div class="${view === "tiles" ? "jtiles" : "card"}" id="list">
        <div class="loading">Reading&hellip;</div></div></div>`);

  document.getElementById("newjob")?.addEventListener("click", () =>
    newJobSheet((jobId) => { location.hash = `#/job/${jobId}`; },
      opts.newKind ?? "job"));

  p.bind((k) => { sort = k; void renderJobs(opts); });
  /* A different list starts on the week again. Somebody who opened up the
     archive to find one job is not asking for it on every other screen.
     The pile is deliberately NOT part of this: the cold-quotes button
     switches pile and opens up in one press, and both have to stick. */
  const screen = `${opts.heading}:${opts.span ?? ""}`;
  const openedUp = openedOn === screen;
  wireViewToggle("listbar", listKey, () => void renderJobs(opts));

  const host = document.getElementById("list");
  if (!host) return;
  try {
    /**
     * A TILE MUST NOT LAND ON A SHORTER PAGE.
     *
     * listJobs defaulted to 100 rows, so All time -> Done said 1,651 on
     * the railway and showed a hundred. A count you cannot click back to
     * is a number nobody can check, and a page that quietly stops at a
     * round number is worse than one that says how many it is holding.
     *
     * 500 is the ceiling because a browser painting several thousand rows
     * on a phone is its own bug. Past that the page says so out loud.
     */
    const CEILING = 500;
    const jobs = opts.ids
      ? await jobsByIds(await opts.ids())
      : await listJobs({
          ...(opts.statuses ? { statuses: opts.statuses } : {}),
          ...(opts.span
            ? { since: spanFrom(opts.span),
                sinceOn: DATED_BY[opts.statuses?.[0] ?? ""] ?? "created_at" }
            : {}),
          order: orderFor(sort, { date: "created_at" }),
          limit: CEILING,
        });
    /**
     * THE BAND IS BUILT FROM THE QUOTES IN FRONT OF YOU, with a count on
     * each, so an empty pile is visible as a zero rather than as a button
     * that does nothing. Cancelled is offered even when the list holds
     * none, because it is the one pile that lives outside the Quotes
     * status and somebody looking for it needs a way in.
     */
    const piled = quoteBand ? byPile(jobs, quotePile) : jobs;

    // The date that matters is the one the list is already sorted by.
    const dated = (j: JobRow) => Date.parse(
      j.scheduled_at ?? j.completed_at ?? j.created_at);
    const cut = Date.now() - WEEK_MS;
    const older = piled.filter((j) => dated(j) < cut).length;
    const shown = openedUp || !older ? piled : piled.filter((j) => dated(j) >= cut);

    if (quoteBand) {
      const bar = document.getElementById("qstates");
      if (bar) {
        const n = (k: QuoteState) => jobs.filter((j) => stateOf(j) === k).length;
        bar.innerHTML = `<div class="crewbar">
          <button class="crewpill${quotePile ? "" : " on"}" data-q="">All
            <i class="qn">${jobs.length}</i></button>
          ${QUOTE_STATES.filter((st) => st.key !== "cancelled").map((st) =>
            `<button class="crewpill${quotePile === st.key ? " on" : ""}"
              data-q="${st.key}" title="${esc(st.blurb)}">${esc(st.label)}
              <i class="qn">${n(st.key)}</i></button>`).join("")}
        </div>`;
        /**
         * THE MONEY NOBODY HAS SENT, SAID OUT LOUD.
         *
         * The counts above are per state, and a count of 40 does not read
         * as urgent. What reads as urgent is the money and the age: this
         * list sorts newest first and folds anything over a week behind a
         * button, so the most neglected quotes are the hardest to reach --
         * the screen is built for recency and the problem is neglect.
         *
         * Tapping it filters to exactly those, so the number and the list
         * cannot disagree.
         */
        const cold = jobs.filter(isCold);
        if (cold.length) {
          const worth = cold.reduce((n, j) => n + j.totals.total, 0);
          const oldest = cold.reduce((a, b) =>
            Date.parse(a.created_at) < Date.parse(b.created_at) ? a : b);
          bar.insertAdjacentHTML("beforeend", `
            <button class="coldbar" id="coldbtn">
              <span class="coldn">${cold.length}</span>
              <span>never sent, worth <b>${money(worth)}</b><br>
                <i>oldest priced ${esc(since(oldest.created_at))}</i></span>
            </button>`);
          document.getElementById("coldbtn")?.addEventListener("click", () => {
            quotePile = "not_sent";
            openedOn = screen;
            void renderJobs(opts);
          });
        }

        bar.querySelectorAll<HTMLButtonElement>("[data-q]").forEach((b) =>
          b.addEventListener("click", () => {
            quotePile = (b.dataset["q"] ?? "") as QuoteState | "";
            void renderJobs(opts);
          }));
      }
    }

    markDupes(shown);
    await markAdvisors(shown);
    host.innerHTML = (shown.length
      ? shown.map(view === "tiles" ? tile : row).join("")
      : `<div class="empty">Nothing in the last week${
          quotePile ? ` ${esc(quoteStateLabel(quotePile).toLowerCase())}` : ""}.${
          older ? "" : ` Nothing ${esc(opts.emptyWord)} at all.`}</div>`)
      + (older
        ? openedUp
          ? `<button class="minemore" id="foldbtn">Back to the last week</button>`
          : `<button class="minemore" id="olderbtn">Show ${older} older
               ${older === 1 ? "one" : "ones"}</button>`
        : "");

    document.getElementById("foldbtn")?.addEventListener("click", () => {
      openedOn = "";
      void renderJobs(opts);
    });

    document.getElementById("olderbtn")?.addEventListener("click", () => {
      openedOn = screen;
      void renderJobs(opts);
    });

    wireArchive(() => void renderJobs(opts));
    void fillThumbs(host);

    /**
     * THE BOX SEARCHES THE SHOP, NOT THE PAGE.
     *
     * Anthony: "search bars aren't working well or at all either."
     *
     * It was filtering the rows already on screen, which is not what a
     * search box means. This list loads a page and the shop has 1,953
     * jobs, so a customer's name found them when they happened to be in
     * the window and found nothing when they were not — with no way to
     * tell which had happened. A box that quietly misses the record you
     * know exists is worse than none, because you conclude the record is
     * gone.
     *
     * Two characters filters what is drawn, which is instant and right for
     * narrowing what you can already see. Three or more asks the database
     * across the job number, the title, the complaint in the customer's
     * own words, their name and phone, and the car down to its plate and
     * VIN. Every word has to appear somewhere, in any order.
     */
    wireFindBar({
      hostId: "findhost", listId: "list", noun: "job",
      deepSearch: async (term) => {
        const sb2 = await db();
        /**
         * SEARCH DOES NOT INHERIT THE PILE YOU ARE STANDING IN.
         *
         * Anthony: "i can't search by job number much, or find 1622."
         *
         * This passed the page's own status list to the search, and the
         * Jobs page's list is scheduled, in progress, completed, invoiced
         * and paid. Cancelled is not on it. So nothing cancelled and
         * nothing ARCHIVED could be found from the one screen with a
         * search box on it, and the Quotes page could only ever find
         * estimates. Job 1622 was cancelled on 5 September and had been
         * unfindable ever since, which is exactly the disappearance the
         * archive button was supposed to be safe from: "a job archived by
         * mistake is still there to be found and reopened."
         *
         * Typing a number is not filtering, it is looking for one thing.
         * So the search asks the whole book, and the row says which pile
         * each answer came from — every row already carries its status.
         */
        const { data, error } = await sb2.rpc("search_jobs", {
          p_term: term,
          p_statuses: null,
          p_limit: 200,
        });
        if (error) throw new Error(error.message);
        const ids = ((data ?? []) as Array<{ id: string }>).map((r) => r.id);
        const found = await jobsByIds(ids);
        const host2 = document.getElementById("list");
        markDupes(found);
        await markAdvisors(found);
        if (host2) {
          /* Said out loud, because the answer now comes from outside the
             screen somebody is looking at and a result that contradicts
             the page's own heading needs explaining once. */
          const closed = found.filter((f) => f.status === "cancelled").length;
          host2.innerHTML = found.length
            ? `<div class="findnote">Searched every job in the shop${
                closed ? `, including ${closed} cancelled or archived` : ""}.</div>`
              + found.map(view === "tiles" ? tile : row).join("")
            : `<div class="empty">Nothing anywhere matches
                 &ldquo;${esc(term)}&rdquo;.</div>`;
          wireArchive(() => void renderJobs(opts));
          void fillThumbs(host2);
        }
        return found.length;
      },
      redraw: () => void renderJobs(opts),
    });
    const n = document.getElementById("count");
    if (n) {
      n.textContent = !jobs.length ? ""
        : jobs.length >= CEILING
          ? `first ${jobs.length} — narrow the window to see the rest`
          : `${jobs.length} shown`;
    }
  } catch (e) {
    host.innerHTML = `<div class="empty bad">That would not load — ${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/**
 * Landed on from a railway stage: the SAME set that produced the number,
 * same rule and same window. A count you cannot click back to is a number
 * nobody can check — and two of them had drifted apart.
 *
 * DONE now means "got finished in the period", so the page has to accept
 * a job that has since been billed or paid, exactly as the tile does.
 *
 * PAID is decided by when the money arrived, which is a fact about a
 * payment and not about a job, so that one resolves its ids first.
 */
export function jobsPage(status?: string, span?: string): Promise<void> {
  if (!status) {
    /**
     * JOBS IS WORK THAT WAS AGREED. Anthony: "Jobs, should be only
     * consisting of quotes that were approved and scheduled."
     *
     * It listed everything, estimates included, so the same price appeared
     * on Quotes and on Jobs and the two counts never agreed. A quote is a
     * proposal and a job is a commitment, and putting them on one screen
     * loses the only distinction the shop actually runs on.
     *
     * Cancelled is out for the same reason: it never became work.
     */
    return renderJobs({
      statuses: ["scheduled", "in_progress", "completed", "invoiced", "paid"],
      heading: "Jobs",
      blurb: "Approved, booked, and everything since.",
      emptyWord: "on the board",
    });
  }
  const label = (STATUS_LABEL as Record<string, string>)[status] ?? status;
  const sp = (SPANS.find((x) => x.key === span)?.label ?? "All time").toLowerCase();

  if (status === "paid") {
    return renderJobs({
      ids: () => jobsPaidIn(spanFrom((span as Span) ?? "all")),
      heading: "Paid",
      blurb: `From the railway — money that arrived, ${sp}.`,
      emptyWord: "settled in this period",
    });
  }

  /* The same widening Done needed, for the same reason: Booked counts
     the appointments in the window, so the page has to accept a job that
     has since been started, finished or paid. The first status in the
     list is the one that picks the date column, so it stays first. */
  return renderJobs({
    statuses: status === "completed"
      ? ["completed", "invoiced", "paid"] as Status[]
      : status === "scheduled"
      ? ["scheduled", "in_progress", "completed", "invoiced", "paid"] as Status[]
      : [status as Status],
    span: (span as Span) ?? "all",
    heading: label,
    blurb: `From the railway — ${label.toLowerCase()}, ${sp}.`,
    emptyWord: "here",
  });
}

/** Jobs whose invoice took money in the window, each once. */
async function jobsPaidIn(from: string | null): Promise<string[]> {
  const sb = await db();
  let pq = sb.from("payments").select("invoice_id").limit(5000);
  if (from) pq = pq.gte("received_at", from);
  const { data: pays } = await pq;
  const invIds = [...new Set(((pays ?? []) as Array<{ invoice_id: string | null }>)
    .map((r) => r.invoice_id).filter((x): x is string => Boolean(x)))];
  if (!invIds.length) return [];

  // Chunked: a list in a URL is a header, and undici refuses one past 16KB.
  const jobIds = new Set<string>();
  for (let i = 0; i < invIds.length; i += 120) {
    const { data } = await sb.from("invoices")
      .select("job_id").in("id", invIds.slice(i, i + 120));
    for (const r of (data ?? []) as Array<{ job_id: string | null }>) {
      if (r.job_id) jobIds.add(r.job_id);
    }
  }
  return [...jobIds];
}

/** The same job shape the list already renders, fetched by id. */
async function jobsByIds(ids: string[]): Promise<JobRow[]> {
  if (!ids.length) return [];
  const out: JobRow[] = [];
  for (let i = 0; i < ids.length; i += 120) {
    out.push(...await listJobs({ ids: ids.slice(i, i + 120), limit: 200 }));
  }
  return out;
}

/**
 * Where one quote is. The row shape the list already holds carries
 * everything this needs, so nothing extra is fetched to answer it.
 */
export const stateOf = (j: JobRow): QuoteState => quoteStateOf({
  status: j.status,
  quoteExpiresAt: j.quote_expires_at,
  changesRequestedAt: j.changes_requested_at,
  hasAuthorization: (j.job_authorizations ?? []).length > 0,
});

const byPile = (jobs: JobRow[], pile: QuoteState | ""): JobRow[] =>
  pile ? jobs.filter((j) => stateOf(j) === pile) : jobs;

export const quotesPage = () =>
  renderJobs({
    statuses: ["estimate"],
    newWord: "New quote",
    newKind: "quote",
    heading: "Quotes",
    blurb: "Priced, and waiting on a yes.",
    emptyWord: "waiting on a customer",
  });
